import React from 'react';
import Box from '@mui/material/Box';
import { keyframes } from '@mui/system';
import { useAppSelector } from '../../redux/store';
import { AnaAvatarStyle } from '../../redux/slices/platformSettings';

const rotate = keyframes`
	0% { transform: rotate(0deg); }
	100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
	0%, 100% { opacity: 0.8; transform: scale(1); }
	50% { opacity: 1; transform: scale(1.05); }
`;

const shimmer = keyframes`
	0% { background-position: 0% 50%; }
	50% { background-position: 100% 50%; }
	100% { background-position: 0% 50%; }
`;

const ripple = keyframes`
	0% { transform: scale(0.8); opacity: 1; }
	100% { transform: scale(2.2); opacity: 0; }
`;

const drift = keyframes`
	0% { background-position: 0% 0%; }
	25% { background-position: 100% 50%; }
	50% { background-position: 50% 100%; }
	75% { background-position: 0% 50%; }
	100% { background-position: 0% 0%; }
`;

const flicker = keyframes`
	0%, 100% { opacity: 0.7; filter: brightness(1); }
	20% { opacity: 1; filter: brightness(1.3); }
	40% { opacity: 0.85; filter: brightness(1.1); }
	60% { opacity: 1; filter: brightness(1.2); }
	80% { opacity: 0.9; filter: brightness(1); }
`;

interface AnaAvatarProps {
	size?: number;
	animated?: boolean;
	style?: AnaAvatarStyle;
}

const NebulaAvatar: React.FC<{ size: number; animated: boolean }> = ({ size, animated }) => (
	<Box
		sx={{
			position: 'relative',
			width: size,
			height: size,
			borderRadius: '50%',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			background: 'linear-gradient(135deg, #001E45 0%, #4a54c4 40%, #BF0A30 70%, #D9113C 100%)',
			backgroundSize: '200% 200%',
			animation: animated ? `${shimmer} 4s ease infinite` : 'none',
			boxShadow: (theme) =>
				theme.palette.mode === 'dark'
					? '0 0 12px rgba(74, 84, 196, 0.4), 0 0 24px rgba(191, 10, 48, 0.2)'
					: '0 2px 8px rgba(0, 30, 69, 0.3)',
			'&::before': {
				content: '""',
				position: 'absolute',
				inset: 0,
				borderRadius: '50%',
				background: 'conic-gradient(from 0deg, transparent 0%, rgba(255,255,255,0.15) 25%, transparent 50%, rgba(74,84,196,0.2) 75%, transparent 100%)',
				animation: animated ? `${rotate} 6s linear infinite` : 'none'
			}
		}}
	/>
);

const AuroraAvatar: React.FC<{ size: number; animated: boolean }> = ({ size, animated }) => (
	<Box
		sx={{
			position: 'relative',
			width: size,
			height: size,
			borderRadius: '50%',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			background: 'linear-gradient(160deg, #001E45 0%, #1a9c54 30%, #4a54c4 60%, #3ddb80 100%)',
			backgroundSize: '300% 300%',
			animation: animated ? `${drift} 6s ease infinite` : 'none',
			boxShadow: (theme) =>
				theme.palette.mode === 'dark'
					? '0 0 14px rgba(26, 156, 84, 0.4), 0 0 28px rgba(74, 84, 196, 0.2)'
					: '0 2px 10px rgba(26, 156, 84, 0.35)',
			overflow: 'hidden',
			'&::before': {
				content: '""',
				position: 'absolute',
				inset: 0,
				borderRadius: '50%',
				background: 'radial-gradient(ellipse at 30% 20%, rgba(61,219,128,0.4) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(106,162,255,0.3) 0%, transparent 50%)',
				animation: animated ? `${pulse} 4s ease-in-out infinite` : 'none'
			},
			'&::after': {
				content: '""',
				position: 'absolute',
				width: '120%',
				height: '40%',
				top: '15%',
				left: '-10%',
				background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
				borderRadius: '50%',
				filter: 'blur(2px)',
				animation: animated ? `${shimmer} 3s ease infinite` : 'none',
				backgroundSize: '200% 100%'
			}
		}}
	>
		<Box sx={{
			width: size * 0.35,
			height: size * 0.35,
			borderRadius: '50%',
			background: 'radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(61,219,128,0.6) 60%, transparent 100%)',
			boxShadow: '0 0 8px rgba(61,219,128,0.5)'
		}} />
	</Box>
);

const PrismAvatar: React.FC<{ size: number; animated: boolean }> = ({ size, animated }) => (
	<Box
		sx={{
			position: 'relative',
			width: size,
			height: size,
			borderRadius: '50%',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			background: 'conic-gradient(from 0deg, #BF0A30, #c98a0c, #1a9c54, #4a54c4, #D9113C, #BF0A30)',
			animation: animated ? `${rotate} 8s linear infinite` : 'none',
			boxShadow: (theme) =>
				theme.palette.mode === 'dark'
					? '0 0 16px rgba(201, 138, 12, 0.3), 0 0 32px rgba(74, 84, 196, 0.2)'
					: '0 2px 12px rgba(0, 30, 69, 0.25)',
			'&::before': {
				content: '""',
				position: 'absolute',
				inset: '2px',
				borderRadius: '50%',
				background: (theme) =>
					theme.palette.mode === 'dark'
						? 'radial-gradient(circle, #1a1a2e 40%, transparent 100%)'
						: 'radial-gradient(circle, #ffffff 40%, transparent 100%)'
			}
		}}
	>
		<Box sx={{
			position: 'relative',
			width: size * 0.5,
			height: size * 0.5,
			borderRadius: '50%',
			background: 'conic-gradient(from 180deg, #4a54c4, #D9113C, #c98a0c, #1a9c54, #4a54c4)',
			animation: animated ? `${rotate} 4s linear infinite reverse` : 'none',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center'
		}}>
			<Box sx={{
				width: size * 0.22,
				height: size * 0.22,
				borderRadius: '50%',
				background: (theme) =>
					theme.palette.mode === 'dark'
						? '#fefefe'
						: '#001E45',
				boxShadow: (theme) =>
					theme.palette.mode === 'dark'
						? '0 0 8px rgba(254,254,254,0.6)'
						: '0 0 6px rgba(74,84,196,0.5)'
			}} />
		</Box>
	</Box>
);

const PulseAvatar: React.FC<{ size: number; animated: boolean }> = ({ size, animated }) => (
	<Box
		sx={{
			position: 'relative',
			width: size,
			height: size,
			borderRadius: '50%',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			background: (theme) =>
				theme.palette.mode === 'dark'
					? 'radial-gradient(circle, #4a54c4 0%, #001E45 100%)'
					: 'radial-gradient(circle, #4a54c4 0%, #001E45 100%)',
			boxShadow: '0 0 12px rgba(74, 84, 196, 0.4)',
			overflow: 'hidden',
			'&::before': animated ? {
				content: '""',
				position: 'absolute',
				width: '100%',
				height: '100%',
				borderRadius: '50%',
				border: '2px solid rgba(106,162,255,0.6)',
				animation: `${ripple} 2s ease-out infinite`
			} : {},
			'&::after': animated ? {
				content: '""',
				position: 'absolute',
				width: '100%',
				height: '100%',
				borderRadius: '50%',
				border: '2px solid rgba(106,162,255,0.4)',
				animation: `${ripple} 2s ease-out infinite 0.6s`
			} : {}
		}}
	>
		<Box sx={{
			width: size * 0.4,
			height: size * 0.4,
			borderRadius: '50%',
			background: 'radial-gradient(circle, #6aa2ff 0%, #4a54c4 100%)',
			animation: animated ? `${pulse} 2s ease-in-out infinite` : 'none',
			boxShadow: '0 0 10px rgba(106,162,255,0.7)'
		}} />
	</Box>
);

const EmberAvatar: React.FC<{ size: number; animated: boolean }> = ({ size, animated }) => (
	<Box
		sx={{
			position: 'relative',
			width: size,
			height: size,
			borderRadius: '50%',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			background: 'radial-gradient(circle at 30% 30%, #D9113C 0%, #BF0A30 40%, #001E45 100%)',
			animation: animated ? `${flicker} 3s ease infinite` : 'none',
			boxShadow: (theme) =>
				theme.palette.mode === 'dark'
					? '0 0 16px rgba(217, 17, 60, 0.5), 0 0 32px rgba(191, 10, 48, 0.3)'
					: '0 2px 12px rgba(191, 10, 48, 0.4)',
			overflow: 'hidden',
			'&::before': {
				content: '""',
				position: 'absolute',
				inset: 0,
				borderRadius: '50%',
				background: 'radial-gradient(circle at 60% 70%, rgba(201,138,12,0.5) 0%, transparent 40%)',
				animation: animated ? `${pulse} 2.5s ease-in-out infinite` : 'none'
			}
		}}
	>
		<Box sx={{
			width: size * 0.35,
			height: size * 0.35,
			borderRadius: '50%',
			background: 'radial-gradient(circle, #c98a0c 0%, rgba(217,17,60,0.8) 100%)',
			boxShadow: '0 0 8px rgba(201,138,12,0.7)',
			animation: animated ? `${flicker} 2s ease infinite 0.5s` : 'none'
		}} />
	</Box>
);

const AVATAR_COMPONENTS: Record<AnaAvatarStyle, React.FC<{ size: number; animated: boolean }>> = {
	nebula: NebulaAvatar,
	aurora: AuroraAvatar,
	prism: PrismAvatar,
	pulse: PulseAvatar,
	ember: EmberAvatar
};

const AnaAvatar: React.FC<AnaAvatarProps> = ({ size = 32, animated = true, style: styleProp }) => {
	const storeStyle = useAppSelector((state) => state.platformSettings.anaAvatarStyle);
	const avatarStyle = styleProp || storeStyle;
	const AvatarComponent = AVATAR_COMPONENTS[avatarStyle] || NebulaAvatar;
	return <AvatarComponent size={size} animated={animated} />;
};

export default AnaAvatar;
